import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import { useBlogPosts, useAllBlogPosts } from "@/hooks/useBlogPosts";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, ArrowRight, User, Plus, Search, ChevronLeft, ChevronRight, EyeOff } from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";

const POSTS_PER_PAGE = 10;

export default function Blog() {
  const { isAdmin } = useAdminAuth();
  const { data: publicPosts, isLoading: publicLoading } = useBlogPosts();
  const { data: allPosts, isLoading: allLoading } = useAllBlogPosts();
  const [searchParams, setSearchParams] = useSearchParams();

  const posts = isAdmin ? allPosts : publicPosts;
  const isLoading = isAdmin ? allLoading : publicLoading;

  const search = searchParams.get("search") ?? "";
  const activeTag = searchParams.get("tag") ?? null;
  const page = parseInt(searchParams.get("page") ?? "1", 10);

  // Collect all unique tags
  const allTags = useMemo(() => {
    if (!posts) return [];
    const tagSet = new Set<string>();
    posts.forEach((p) => p.tags.forEach((t: string) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter posts (already ordered latest-first from query)
  const filtered = useMemo(() => {
    if (!posts) return [];
    return posts.filter((p) => {
      const matchesSearch =
        !search ||
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(search.toLowerCase());
      const matchesTag = !activeTag || p.tags.includes(activeTag);
      return matchesSearch && matchesTag;
    });
  }, [posts, search, activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);

  const setSearch = (v: string) => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      v ? n.set("search", v) : n.delete("search");
      n.delete("page");
      return n;
    });
  };
  const handleTag = (t: string | null) => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      t ? n.set("tag", t) : n.delete("tag");
      n.delete("page");
      return n;
    });
  };
  const setPage = (p: number) => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.set("page", String(p));
      return n;
    });
  };

  return (
    <PublicLayout>
      <SEO
        title="Blog"
        description="Insights on requirements engineering, agile best practices, and AI-powered product management from the Reqqy team."
        canonical="/blog"
      />
      <section className="py-20 md:py-28">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">
              The Reqqy <span className="text-gradient">Blog</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Practical insights on writing better requirements, reducing rework, and shipping faster.
            </p>
            {isAdmin && (
              <Button asChild className="mt-4 gradient-primary text-primary-foreground">
                <Link to="/blog/new">
                  <Plus className="w-4 h-4 mr-1" />
                  New Post
                </Link>
              </Button>
            )}
          </div>

          {/* Search & Tag Filters */}
          <div className="max-w-4xl mx-auto mb-8 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={activeTag === null ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => handleTag(null)}
                >
                  All
                </Badge>
                {allTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={activeTag === tag ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => handleTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="max-w-4xl mx-auto space-y-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex flex-col md:flex-row gap-6 p-6 rounded-2xl glass">
                  <Skeleton className="w-full md:w-64 h-44 rounded-xl" />
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="max-w-4xl mx-auto text-center py-12">
              <p className="text-muted-foreground">No articles found. Try a different search or filter.</p>
            </div>
          ) : (
            <>
              <div className="max-w-4xl mx-auto space-y-8">
                {paginated.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex flex-col md:flex-row gap-6 p-6 rounded-2xl glass card-hover animate-fade-in relative"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Draft badge for admins */}
                    {isAdmin && !post.published && (
                      <div className="absolute top-3 right-3 z-10">
                        <Badge variant="destructive" className="gap-1 text-xs">
                          <EyeOff className="w-3 h-3" />
                          Draft
                        </Badge>
                      </div>
                    )}
                    <div className="w-full md:w-64 h-44 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          className="w-full h-full object-contain bg-background dark:bg-muted"
                          loading="lazy"
                        />
                      ) : (
                        <div className="text-6xl font-display font-bold text-primary/20">
                          {String(index + 1).padStart(2, "0")}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.tags.slice(0, 3).map((tag: string) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm line-clamp-2">{post.excerpt}</p>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author_name}
                          </span>
                          {post.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(post.published_at), "MMM d, yyyy")}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {Math.ceil(post.content.split(" ").length / 200)} min read
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <Button
                      key={p}
                      variant={p === currentPage ? "default" : "outline"}
                      size="icon"
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
